import React, { useEffect, useRef, useState } from "react";
import { CardContent } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import LoadingSpinner from "../../../LoadingSpinner";
import {
  useGetAllCampaignQuery,
  useGetCampaignLeadReportQuery,
  useLazyGetCampaignLeadReportDownloadQuery,
} from "../../../../Services/reports/tickets/ReportTickets";
import { toast } from "react-toastify";
import CsvDownloader from "react-csv-downloader";
import { AUTH_TOKEN } from "../../../../constants";

const getTodayDateString = () => new Date().toISOString().split("T")[0];

const isCurrentDateRange = (from: string, to: string) => {
  const today = getTodayDateString();
  return from === today && to === today;
};

type ReportMode = "live" | "historical" | null;

const DEFAULT_STATIC_COLUMNS: GridColDef[] = [
  {
    field: "srNo",
    headerName: "Sr.No",
    flex: 1,
    minWidth: 100,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "createdDateTime",
    headerName: "Created Date and Time",
    flex: 1,
    minWidth: 200,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "leadId",
    headerName: "Lead Id",
    flex: 1,
    minWidth: 150,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "accountName",
    headerName: "Account Name",
    flex: 1,
    minWidth: 200,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "firstName",
    headerName: "First Name",
    flex: 1,
    minWidth: 150,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "lastName",
    headerName: "Last Name",
    flex: 1,
    minWidth: 150,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "email",
    headerName: "Email",
    flex: 1,
    minWidth: 200,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "phoneNumber",
    headerName: "Phone Number",
    flex: 1,
    minWidth: 200,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "currentAssignee",
    headerName: "Current Assignee",
    flex: 1,
    minWidth: 200,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "lastUpdatedDateTime",
    headerName: "Last Updated Date and Time",
    flex: 1,
    minWidth: 200,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    minWidth: 150,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "internalNotes",
    headerName: "Internal Notes",
    flex: 1,
    minWidth: 200,
    align: "center",
    headerAlign: "center",
  },
];

const isDate = (value: unknown): boolean => {
  if (typeof value !== "string") return false;
  const datePatterns = [
    /^\d{4}-\d{1,2}-\d{1,2}$/,
    /^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{1,2}(?::\d{1,2})?$/,
    /^\d{4}-\d{1,2}-\d{1,2}T\d{1,2}:\d{1,2}(?::\d{1,2})?/,
    /^\d{1,2}\/\d{1,2}\/\d{4}$/,
    /^\d{4}\/\d{1,2}\/\d{1,2}$/,
  ];
  return datePatterns.some((p) => p.test(value)) && !isNaN(Date.parse(value));
};

const formatDateTime = (value: string) => {
  if (!value) return "-";
  try {
    let normalizedValue = value;
    if (/^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{1,2}(?::\d{1,2})?$/.test(value)) {
      normalizedValue = value.replace(
        /(\d{4})-(\d{1,2})-(\d{1,2}) (\d{1,2}):(\d{1,2}):?(\d{0,2})/,
        (_: string, y: string, M: string, d: string, H: string, m: string, s: string) => {
          const pad = (n: string) => n.padStart(2, "0");
          const sec = s ? `:${pad(s)}` : "";
          return `${y}-${pad(M)}-${pad(d)} ${pad(H)}:${pad(m)}${sec}`;
        }
      );
    }
    const date = new Date(normalizedValue);
    if (isNaN(date.getTime())) return value;
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(value.trim())) {
      return `${day}/${month}/${year}`;
    }
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return value;
  }
};

function buildCampaignLeadColumnsAndRows(
  results: any[],
  apiPage: number,
  pageSize: number
): { columns: GridColDef[]; rows: any[] } {
  if (!Array.isArray(results) || results.length === 0) {
    return { columns: [...DEFAULT_STATIC_COLUMNS], rows: [] };
  }

  const staticColumns = [...DEFAULT_STATIC_COLUMNS];

  const dynamicFieldKeys = new Set<string>();
  results.forEach((item) => {
    if (item.dynamic_fields) {
      Object.keys(item.dynamic_fields).forEach((key) => dynamicFieldKeys.add(key));
    }
  });

  const dynamicColumns: GridColDef[] = Array.from(dynamicFieldKeys).map((key) => ({
    field: key,
    headerName: key,
    flex: 1,
    minWidth: 150,
    align: "center",
    headerAlign: "center",
  }));

  const columns = [...staticColumns, ...dynamicColumns];

  const baseIndex = (apiPage - 1) * pageSize;

  const transformedRows = results.map((item, index) => {
    const dynamicFields = item.dynamic_fields
      ? Object.fromEntries(
          Object.entries(item.dynamic_fields).map(([key, value]) => {
            const v = value as string;
            const formattedValue =
              v && isDate(v) ? formatDateTime(v) : (value ?? " ");
            return [key, formattedValue];
          })
        )
      : {};

    return {
      id: item.id ?? `${baseIndex + index}`,
      srNo: baseIndex + index + 1,
      createdDateTime: item.created ? formatDateTime(item.created) : "-",
      leadId: item.id,
      accountName: item.account_name || "-",
      firstName: item.first_name || "-",
      lastName: item.last_name || "-",
      email: item.email || "-",
      phoneNumber: item.phone_number || "-",
      currentAssignee: item.assignee?.username || "-",
      lastUpdatedDateTime: item.updated ? formatDateTime(item.updated) : "-",
      status: item.status_type || "-",
      internalNotes: item.internal_notes || "-",
      ...dynamicFields,
    };
  });

  return { columns, rows: transformedRows };
}

const CampaignLeadReport = () => {
  const navigate = useNavigate();
  const downloaderRef = useRef(null);

  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const { data: campaignData, isLoading: campaignDataLoading } =
    useGetAllCampaignQuery({});
  const [params, setParams] = useState<any>(null);
  const [dataRows, setDataRows] = useState<any[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [reportFetching, setReportFetching] = useState(false);
  const [reportMode, setReportMode] = useState<ReportMode>(null);
  const [triggerCampaignLeadDownload, { isFetching: downloadLoading }] =
    useLazyGetCampaignLeadReportDownloadQuery();
  const [totalRowsFromApi, setTotalRowsFromApi] = useState<number | null>(null);
  const [campaignSelected, setCampaignSelected] = useState<any>([]);
  const [columns, setColumns] = useState<GridColDef[]>(DEFAULT_STATIC_COLUMNS);

  const [selectQueue, setSelectQueue] = useState<any>("");

  const reportingBaseUrl =
    process.env.REACT_APP_REPORTING_SERVER_URL || "https://reporting.bluewhirl.org";

  const {
    data: liveCampaignLeadReport,
    isLoading: liveReportLoading,
    isFetching: liveReportFetching,
    error: liveReportError,
  } = useGetCampaignLeadReportQuery(params, {
    skip: !params || reportMode !== "live",
  });

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });

  const handleDateFromChange = (event: any) => {
    setDateFrom(event.target.value);
  };

  const handleDateToChange = (event: any) => {
    setDateTo(event.target.value);
  };

  const handleQueueChange = (event: any) => {
    setSelectQueue(event.target.value);
  };

  useEffect(() => {
    const dropdownHeadings = document.querySelectorAll(".dropdown-heading");
    dropdownHeadings.forEach((heading: any) => {
      heading.style.height = "50px";
    });
  }, []);

  useEffect(() => {
    if (campaignData && Array.isArray(campaignData.results)) {
      const transformedData = campaignData.results.map((queue) => ({
        id: queue.id,
        name: queue.name,
      }));
      setCampaignSelected(transformedData);
    }
  }, [campaignData]);

  useEffect(() => {
    if (reportMode !== "live") return;

    if (liveReportError) {
      setDataRows([]);
      setColumns(DEFAULT_STATIC_COLUMNS);
      setLocalLoading(false);
      return;
    }

    if (liveCampaignLeadReport === undefined) {
      setLocalLoading(false);
      return;
    }

    if (Array.isArray(liveCampaignLeadReport)) {
      const { columns: builtColumns, rows } = buildCampaignLeadColumnsAndRows(
        liveCampaignLeadReport,
        1,
        liveCampaignLeadReport.length
      );
      setColumns(builtColumns);
      setDataRows(rows);
    } else {
      setDataRows([]);
      setColumns(DEFAULT_STATIC_COLUMNS);
    }

    setLocalLoading(false);
  }, [liveCampaignLeadReport, liveReportError, reportMode]);

  useEffect(() => {
    if (!params || reportMode !== "historical") {
      if (reportMode !== "historical") {
        setTotalRowsFromApi(null);
      }
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN);
    if (!token) {
      toast.error("Session expired or not logged in. Please sign in again.");
      return;
    }

    const controller = new AbortController();
    setLocalLoading(true);
    setReportFetching(true);

    const queryParams: Record<string, string | number | boolean> = {
      from_date: params.gte,
      to_date: params.lte,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
      download: false,
      campaign: params.campaign,
    };

    axios
      .get(`${reportingBaseUrl}/leads/summary`, {
        params: queryParams,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        const data = res?.data;
        const results = Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
              ? data
              : [];
        const totalRows =
          data?.total_rows ?? data?.count ?? data?.total ?? results.length;
        setTotalRowsFromApi(
          typeof totalRows === "number" ? totalRows : results.length
        );

        const serverPage = data?.current_page ?? params.page ?? 1;
        const serverPageSize = data?.page_size ?? params.page_size ?? 20;
        setPaginationModel({
          page: Math.max(0, Number(serverPage) - 1),
          pageSize: Number(serverPageSize),
        });

        const apiPage = Number(serverPage);
        const pageSize = Number(serverPageSize);
        const { columns: builtColumns, rows } = buildCampaignLeadColumnsAndRows(
          results,
          apiPage,
          pageSize
        );
        setColumns(builtColumns);
        setDataRows(rows);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        toast.error(
          err?.response?.data?.message || "Failed to fetch Lead Summary report"
        );
        setDataRows([]);
        setColumns(DEFAULT_STATIC_COLUMNS);
        setTotalRowsFromApi(null);
      })
      .finally(() => {
        setLocalLoading(false);
        setReportFetching(false);
      });

    return () => controller.abort();
  }, [params, reportingBaseUrl, reportMode]);

  useEffect(() => {
    if (params && reportMode === "live") {
      setLocalLoading(true);
    }
  }, [params, reportMode]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!dateFrom) {
      toast.error('Please select "From Date"');
      return;
    }
    if (!dateTo) {
      toast.error('Please select "To Date"');
      return;
    }
    if (new Date(dateFrom) > new Date(dateTo)) {
      toast.error('"From Date" cannot be later than "To Date"');
      return;
    }

    if (!selectQueue || selectQueue === "") {
      toast.error("Please select a campaign from the dropdown");
      return;
    }

    const selectedQueue = campaignSelected.find((queue: any) => queue.id === selectQueue);
    if (!selectedQueue) {
      toast.error("Invalid campaign selected");
      return;
    }

    const isLive = isCurrentDateRange(dateFrom, dateTo);
    const queryParams: any = {
      gte: dateFrom,
      lte: dateTo,
      campaign: selectedQueue.id,
    };

    if (isLive) {
      queryParams.timestamp = new Date().getTime();
    } else {
      queryParams.page = 1;
      queryParams.page_size = paginationModel.pageSize;
    }

    setReportMode(isLive ? "live" : "historical");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setParams(queryParams);
  };

  const handleDownload = async () => {
    if (!params) {
      toast.error("Please apply filters and submit before generating the report.");
      return;
    }

    try {
      await triggerCampaignLeadDownload({
        from_date: params.gte,
        to_date: params.lte,
        page: 1,
        page_size: totalRowsFromApi ?? 99999,
        campaign: params.campaign,
      }).unwrap();
      toast.success("Report generation in progress. Download from exported report section once ready");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to queue report generation.");
    }
  };

  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setSelectQueue("");
    setParams(null);
    setReportMode(null);
    setDataRows([]);
    setTotalRowsFromApi(null);
    setColumns(DEFAULT_STATIC_COLUMNS);
    setPaginationModel({ page: 0, pageSize: 20 });
  };

  const ITEM_HEIGHT = 60;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 150,
      },
    },
  };

  const handlePaginationChange = (newPaginationModel: any) => {
    setPaginationModel(newPaginationModel);
    if (!params || reportMode !== "historical") return;
    const updatedParams = {
      ...params,
      page: newPaginationModel.page + 1,
      page_size: newPaginationModel.pageSize,
    };
    setParams(updatedParams);
  };

  const gridLoading =
    reportMode === "live"
      ? localLoading || liveReportLoading || liveReportFetching
      : localLoading || reportFetching;

  return (
    <div id="kt_app_content_container" className="app-container mx-5">
      <div className="card card-flush">
        {/* Card Header */}
        <div
          className="card-header align-items-center py-1 gap-2 gap-md-5"
          style={{ justifyContent: "flex-start" }}
        >
          <button
            className="btn btn-link mb-2"
            onClick={() => {
              navigate("/reports/campaign/reports-list");
            }}
            style={{ marginRight: "-17px" }}
          >
            <i
              className="bi bi-arrow-90deg-left"
              style={{
                fontSize: "20px",
                color: "black",
                cursor: "pointer",
              }}
            ></i>
          </button>
          <span className="fs-3 text-gray-800 fw-bold ms-3">Lead Summary</span>
        </div>

        {/* Toolbar */}
        <div
          className="card-toolbar flex-row-fluid justify-content-start gap-5 d-flex mb-10"
          style={{ marginLeft: "30px" }}
        >
          <div className="w-100 mw-150px">
            <TextField
              id="date-from"
              label="From"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  max: getTodayDateString(),
                  style: { textTransform: "uppercase" },
                },
              }}
              fullWidth
              value={dateFrom}
              onChange={handleDateFromChange}
            />
          </div>
          <div className="w-100 mw-150px">
            <TextField
              id="date-to"
              label="To"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  max: getTodayDateString(),
                  style: { textTransform: "uppercase" },
                },
              }}
              fullWidth
              value={dateTo}
              onChange={handleDateToChange}
            />
          </div>

          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="Queue-select-label">Campaign</InputLabel>
              <Select
                labelId="Queue-select-label"
                id="Queue-select"
                label="Campaign"
                value={selectQueue}
                onChange={handleQueueChange}
                MenuProps={MenuProps}
              >
                {campaignSelected?.map((queue: any) => (
                  <MenuItem key={queue.id} value={queue.id}>
                    {queue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="ms-auto d-flex gap-3" style={{ marginRight: "20px" }}>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Submit
            </button>

            {reportMode === "live" ? (
              <button
                className="btn btn-primary"
                disabled={!params || dataRows.length === 0}
              >
                <CsvDownloader
                  ref={downloaderRef}
                  filename="Lead-summary-report"
                  datas={dataRows}
                  columns={columns.map((col) => ({
                    id: col.field,
                    displayName: col.headerName,
                  }))}
                  separator=","
                  wrapColumnChar={`"`}
                >
                  Download
                </CsvDownloader>
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={handleDownload}
                disabled={!params || downloadLoading}
              >
                {downloadLoading ? "Loading..." : "Download"}
              </button>
            )}

            <button className="btn btn-link" onClick={resetFilters}>
              <i className="bi bi-funnel fs-2 text-dark"></i>
            </button>
          </div>
        </div>

        <CardContent style={{ width: "100%" }}>
          {gridLoading || campaignDataLoading ? (
            <LoadingSpinner />
          ) : (
            <DataGrid
              rows={dataRows}
              columns={columns}
              pagination
              {...(reportMode === "historical"
                ? {
                    paginationMode: "server" as const,
                    rowCount: totalRowsFromApi ?? 0,
                  }
                : {})}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              loading={gridLoading}
              disableColumnMenu
              pageSizeOptions={[5, 10, 15, 20]}
              sx={{
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "lightgray",
                  "&:last-child": {
                    justifyContent: "flex-end",
                  },
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiTablePagination-select, .MuiTablePagination-actions": {
                  marginBottom: "10px",
                },
                "& .MuiDataGrid-overlayWrapper": {
                  height: "50px",
                },
              }}
            />
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default CampaignLeadReport;
